import * as React from 'react';
import {
  Button,
  getIntrinsicElementProps,
  mergeCallbacks,
  SearchBox,
  slot,
  useMergedRefs,
} from '@fluentui/react-components';
import type { GroundingMenuSearchBoxProps, GroundingMenuSearchBoxState } from './GroundingMenuSearchBox.types';
import { useControllableState } from '@fluentui/react-utilities';
import { bundleIcon, Search20Filled, Search20Regular } from '@fluentui/react-icons';
import { groundingCommandBarClassNames } from '../GroundingCommandBar';
import { GroundingCommandBarOverflowMenuClassNames } from '../GroundingCommandBarOverflowMenu';
import { groundingCommandBarTabClassNames } from '../GroundingCommandBarTab';

const Search = bundleIcon(Search20Filled, Search20Regular);

/**
 * Create the state required to render GroundingMenuSearchBox.
 *
 * The returned state can be modified with hooks such as useGroundingMenuSearchBoxStyles_unstable,
 * before being passed to renderGroundingMenuSearchBox_unstable.
 *
 * @param props - props from this instance of GroundingMenuSearchBox
 * @param ref - reference to root HTMLElement of GroundingMenuSearchBox
 */
export const useGroundingMenuSearchBox_unstable = (
  props: GroundingMenuSearchBoxProps,
  ref: React.Ref<HTMLDivElement>,
): GroundingMenuSearchBoxState => {
  const { defaultExpanded, onExpandedChange } = props;
  const [expanded, setExpanded] = useControllableState<boolean>({
    state: props.expanded,
    initialState: false,
    defaultState: defaultExpanded,
  });

  const searchBox = useExpandingSearchBox({ ...props, expanded, setExpanded });

  const expandButton = !expanded
    ? slot.optional(props.expandButton, {
        defaultProps: {
          icon: <Search />,
          appearance: 'subtle',
        },
        renderByDefault: true,
        elementType: Button,
      })
    : undefined;

  const localRootRef = React.useRef<HTMLDivElement>(null);
  const rootRef = useMergedRefs(ref, localRootRef);
  const state: GroundingMenuSearchBoxState = {
    components: {
      root: 'div',
      expandButton: Button,
      searchBox: SearchBox,
    },
    root: slot.always(
      getIntrinsicElementProps('div', {
        ref: rootRef,
        ...props,
      }),
      { elementType: 'div' },
    ),
    expandButton,
    searchBox,
    expanded,
  };

  const expandOnClick = React.useCallback(() => {
    onExpandedChange?.({ expanded: true });
    setExpanded(true);
  }, [onExpandedChange, setExpanded]);

  if (state.expandButton) {
    state.expandButton.onClick = mergeCallbacks(
      state.expandButton.onClick as React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>,
      expandOnClick,
    );
  }

  return state;
};

// This selector is used to determine if focus is moving out of the SearchBox and into the Tabs. By default the Tabs are in a Tabster arrow group so a `data-tabster-dummy` elementType
// is the first to recieve focus.
const groundingCommandBarTabListInteractiveSelector = `.${groundingCommandBarClassNames.root} .${GroundingCommandBarOverflowMenuClassNames.menuButton},.${groundingCommandBarTabClassNames.tab},[data-tabster-dummy]`;

const useExpandingSearchBox = (
  props: GroundingMenuSearchBoxProps & { expanded: boolean; setExpanded: (expanded: boolean) => void },
) => {
  const { expanded, focusSearchBoxOnExpand = true, collapseOnUseTabs = false, onExpandedChange, setExpanded } = props;

  const searchBox = expanded
    ? slot.optional(props.searchBox, {
        renderByDefault: true,
        elementType: SearchBox,
      })
    : undefined;

  const searchBoxRootSlot = searchBox ? slot.always(searchBox.root, { elementType: 'span' }) : undefined;

  const searchBoxExpandOnMountRef = useExpandOnMount({ expanded, focusSearchBoxOnExpand });
  const searchBoxRef = useMergedRefs(searchBoxExpandOnMountRef, props.searchBox?.ref);

  // The SearchBox ref goes to the `input` slot by default, so we need a root ref in addition.
  const searchBoxLocalRootRef = React.useRef<HTMLSpanElement>(null);
  const searchBoxRootRef = useMergedRefs(searchBoxLocalRootRef, searchBoxRootSlot?.ref);

  const onSearchBoxBlur: React.FocusEventHandler = React.useCallback(
    event => {
      if (
        // If focus is moving within the search box, don't collapse
        searchBoxLocalRootRef.current?.contains(event.relatedTarget) ||
        // If the user is using the Tabs, don't collapse
        (!collapseOnUseTabs && event.relatedTarget?.matches(groundingCommandBarTabListInteractiveSelector))
      ) {
        return;
      }

      onExpandedChange?.({ expanded: false });
      setExpanded(false);
    },
    [collapseOnUseTabs, onExpandedChange, setExpanded],
  );

  if (searchBox) {
    searchBox.ref = searchBoxRef;
    searchBox.onBlur = mergeCallbacks(searchBox.onBlur, onSearchBoxBlur);

    if (searchBoxRootSlot) {
      searchBoxRootSlot.ref = searchBoxRootRef;
      searchBox.root = searchBoxRootSlot;
    }
  }

  return searchBox;
};

const useExpandOnMount = (props: { expanded: boolean; focusSearchBoxOnExpand: boolean }) => {
  const { expanded, focusSearchBoxOnExpand } = props;
  const [expandedOnMount] = React.useState<boolean>(expanded);
  // If the search box is expanded on mount, we don't want to focus it that render
  // Only if search box started collapsed and was later expanded.
  const initialFocusSuppressed = React.useRef<boolean>(!focusSearchBoxOnExpand || !expandedOnMount);

  const searchBoxCallbackRef = React.useCallback(
    (elem: HTMLSpanElement | null) => {
      if (!elem) {
        return;
      }

      if (expandedOnMount && !initialFocusSuppressed.current) {
        // The search box starts expanded, so we don't want to focus it on first mount
        initialFocusSuppressed.current = true;
        return;
      }

      if (focusSearchBoxOnExpand) {
        elem?.focus();
      }
    },
    [expandedOnMount, focusSearchBoxOnExpand],
  );

  return searchBoxCallbackRef;
};
